/**
 * One deadline formatter for every alert surface (deep review #5).
 *
 * The previous version lived inside `AlertCard` and was day-granular:
 *
 *     const daysUntil = Math.ceil((deadline - now) / 86_400_000);
 *     if (daysUntil < 0) …overdue…
 *
 * Two defects, both fatal for TASK-672 meeting reminders. `Math.ceil` of a small negative
 * fraction is `-0`, and `-0 < 0` is FALSE in JS — so a meeting overdue by anything under 24h
 * fell through to "Due today", in neutral styling. And a meeting starting in 12 minutes
 * rendered "Due tomorrow". A reminder whose whole job is "this meeting is at X" has to be
 * right at minute resolution in both directions.
 *
 * `absolute` is rendered in the BROWSER's timezone on purpose: the RM's own local time is the
 * one the AC asks for, and a server-side string would be wrong for a travelling RM.
 */
import { format } from 'date-fns';

export interface DeadlineInfo {
  /** Local wall-clock, e.g. "Fri 24 Jul, 09:00". */
  absolute: string;
  /** Signed distance, e.g. "in 12 minutes" / "13 hours overdue". */
  relative: string;
  isOverdue: boolean;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** Coarsest unit that still reads honestly: minutes < 1h, hours < 1d, days beyond. */
function magnitude(ms: number): string {
  if (ms < MINUTE) return 'less than a minute';
  if (ms < HOUR) return plural(Math.floor(ms / MINUTE), 'minute');
  if (ms < DAY) return plural(Math.floor(ms / HOUR), 'hour');
  return plural(Math.floor(ms / DAY), 'day');
}

export function formatDeadline(
  deadline?: string | null,
  now: number = Date.now(),
): DeadlineInfo | null {
  if (!deadline) return null;
  const at = new Date(deadline);
  if (Number.isNaN(at.getTime())) return null;

  const diff = at.getTime() - now;
  const isOverdue = diff < 0;
  return {
    absolute: format(at, 'EEE d MMM, HH:mm'),
    relative: isOverdue ? `${magnitude(-diff)} overdue` : `in ${magnitude(diff)}`,
    isOverdue,
  };
}

/** Single-line form for compact surfaces: "Fri 24 Jul, 09:00 · in 12 minutes". */
export function deadlineLine(deadline?: string | null, now?: number): string | null {
  const info = formatDeadline(deadline, now);
  return info && `${info.absolute} · ${info.relative}`;
}
